import { useEffect, useRef, useState } from 'react'
import { PublicReadError, poll } from './eventAthletes'

export interface PolledRead<T> {
  /** The newest answer for the current key, or null before the first (and after the key changes). */
  value: T | null
  /** The endpoint answered that there is nothing to read (a 4xx: an athlete or race the page can't show). */
  missing: boolean
}

const NOTHING = { value: null, missing: false } as const

/**
 * Reads `read` now and every `everyMs` while `key` is non-null and the page is visible (`poll`), as the map's positions
 * are read. A new key starts from nothing, so an old answer never shows under a new question; a failed read without an
 * answer (the network) keeps what was there and tries again on the next tick; a 4xx says it is missing.
 */
export function usePolledRead<T>(key: string | null, read: (signal: AbortSignal) => Promise<T>, everyMs: number): PolledRead<T> {
  const [state, setState] = useState<{ key: string | null; value: T | null; missing: boolean }>({ key, ...NOTHING })
  const readRef = useRef(read)
  readRef.current = read

  useEffect(() => {
    if (key === null) return
    const controller = new AbortController()
    const stop = poll(everyMs, () => {
      readRef
        .current(controller.signal)
        .then((value) => {
          if (!controller.signal.aborted) setState({ key, value, missing: false })
        })
        .catch((failure: unknown) => {
          if (controller.signal.aborted) return
          if (failure instanceof PublicReadError && failure.status >= 400 && failure.status < 500 && failure.status !== 429) {
            setState({ key, value: null, missing: true })
          }
        })
    })
    return () => {
      stop()
      controller.abort()
    }
  }, [key, everyMs])

  return state.key === key ? { value: state.value, missing: state.missing } : NOTHING
}
